import { icons } from 'assets'
import cl from 'classnames'
import {
  IconButton
} from 'components'
import {
  Button,
  H2,
  ImagePortrait,
  ImageSquare,
  NoneLabel,
  SectionContainer, TabsLine, Text1
} from 'components/shared'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { Brand } from 'models'
import React, { useEffect, useState } from 'react'
import {
  BRAND_STORE,
  PROJECT_PAGE_STORE,
  // sourceTypes,
  SourceTypes
} from 'store'
import { getMainImageSrcForObj, getObjExternalId, getObjId } from 'utilities'
import { capitalize } from 'validate.js'
import { ProductPopupSelector } from './ProductPopupSelector'
import styles from './ProductSection.module.scss'
import { ProductSectionProps } from './ProductSection.types'

const ProductSectionComponent = (props: ProductSectionProps) => {
  const {
    [PROJECT_PAGE_STORE]: {
      project: { brands = [], id: projectId },
      hasBrands,
      setProjectProps,
      productsCount
    },
    [BRAND_STORE]: {
      setBrandToView,
      getBrands,
      brandsList,
      clearStoreObservables,
      getBrandByIdAndProject,
      brandToView
    },
    ...rest
  } = props

  const [selectedBrand, setSelectedBrand] = useState(brands.length ? brands[0] : null)
  const [showPopup, setShowPopup] = useState(false)
  const [productSource, setProductSource] = useState<SourceTypes>(SourceTypes.STUDIO)
  const [showProducts,setShowProducts ] = useState(false);
  
  useEffect(() => {
    if(document.URL.substring(document.URL.lastIndexOf('/') + 1) === "create"){
      setShowProducts(false)
    }else{
      setShowProducts(true);
    }

    return () =>{
      setShowProducts(true)
    }

},[])


  const handleAddProductClick = () => {
    setBrandToView()
    getBrands(false, false, productSource)
    setShowPopup(true)
  }

  const onPopupSelectorClose = () => {
    clearStoreObservables()
    setShowPopup(false)
  }

  const handlePopupBrandSelect = (brandName: string) => {
    const { id, externalId } = brandsList.find(b => b.name === brandName)

    const brandId = {
      [SourceTypes.STUDIO]: id,
      [SourceTypes.MARKETPLACE]: externalId,
      [SourceTypes.COCOS]: externalId,
      [SourceTypes.TECH]: externalId
    }[productSource]

    setBrandToView()
    getBrandByIdAndProject(brandId, true, false, productSource, projectId)
  }

  const getSelectedObjectList = () => {
    return (brands.find(b => b.externalId === brandToView?.externalId) || {}).products || []
  }

  const onProductClick = (productId: number) => {
    let selectedProducts = getSelectedObjectList()
    const isProductSelected = Boolean(selectedProducts.find(p => getObjId(p) === productId))

    if (isProductSelected) {
      selectedProducts = selectedProducts.filter(p => getObjId(p) !== productId)
    } else {
      const newProductToAdd = (brandToView?.products || []).find(p => getObjId(p) === productId)
      if (!newProductToAdd) return
      selectedProducts.push(newProductToAdd)
    }

    let cloneBrands: Brand[] = []
    if (selectedProducts.length === 0) {
      cloneBrands = [...brands.filter(b => b.externalId !== brandToView?.externalId)]
    } else {
      const index = brands.findIndex(b => b.externalId === brandToView?.externalId)
      cloneBrands = [...brands]
      if (index < 0) {
        const brandToAdd = { ...brandToView, products: selectedProducts }
        cloneBrands.push(brandToAdd as Brand)
      } else {
        cloneBrands[index].products = selectedProducts
      }
    }

    setProjectProps({ brands: cloneBrands })
  }

  const onProductRemove = (productId: number): void => {
    const indexBrand = brands.findIndex(b => b.externalId === selectedBrand?.externalId)
    const indexProduct = brands[indexBrand]?.products.findIndex(b => b.externalId === productId)
    if (indexProduct === undefined || indexProduct < 0) return
    brands[indexBrand].products.splice(indexProduct, 1)
    if (!brands[indexBrand].products.length) {
      brands.splice(indexBrand, 1)
    }
  }

  // const handleOnPopupSelect = selectedProductsList => {
  //   // console.log('selected product: ', selectedProductsList)
  //   let cloneBrands: Brand[] = []
  //   if (selectedProductsList.length === 0) {
  //     cloneBrands = [...brands.filter(b => b.externalId !== brandToView.externalId)]
  //   } else {
  //     const index = brands
  //       .findIndex(b => b.externalId === selectedProductsList[0].brandExternalId)
  //     cloneBrands = [...brands]
  //     if (index < 0) {
  //       const brandToAdd = { ...brandToView, products: selectedProductsList }
  //       cloneBrands.push(brandToAdd as Brand)
  //     } else {
  //       cloneBrands[index].products = selectedProductsList
  //     }
  //   }

  //   setProjectProps({ brands: cloneBrands })
  // }

  // const isToggleSwitched = productSource === sourceTypes.marketplace
  const onSourceChange = (value: SourceTypes) => {
    // const newValue = isToggleSwitched ? sourceTypes.studio : sourceTypes.marketplace
    setProductSource(value)
    setBrandToView()
    getBrands(false, false, value)
  }

  return (
    <SectionContainer {...rest} extraPaddingBottom={hasBrands}>
      <H2>Products ({productsCount})</H2>
      <Button
        preset='secondary'
        onClick={handleAddProductClick}
        disabled={!showProducts}
      >
        Add a product
      </Button>

      {hasBrands ? (
        <div className={styles.container}>
          <TabsLine<Brand>
            tabs={brands}
            customTabComponent={(brand) => (
              <>
                <ImagePortrait
                  src={getMainImageSrcForObj(brand)}
                  width={20}
                />
                <Text1
                  overflowEllipsis
                  className={cl(styles.brandName)}
                >
                  {brand.name} ({brand.products.length})
                </Text1>
              </>
            )}
            onSelectedTabChange={setSelectedBrand}
          />

          <div className={cl(styles.productsContainer, styles.scrollable)}>
            {selectedBrand != null && selectedBrand.products?.map((p, index) => (
              <div key={index} className={styles.product}>
                {p.removable && (
                  <IconButton
                    icon={icons.closeX}
                    className={styles.tagIcon}
                    onClick={() => onProductRemove(getObjId(p))}
                  />
                )}
                <ImageSquare
                  src={getMainImageSrcForObj(p)}
                  width={100}
                  style={{ borderRadius: 14 }}
                />
                <Text1 overflowEllipsis>{p.name}</Text1>
              </div>
            ))}
          </div>
        </div>
      ) : <NoneLabel label={!showProducts && 
        "Please create project first then you can edit and add products here."} /> 
      }

      {showPopup && (
        <ProductPopupSelector
          sourceSelectProps={{
            options: Object.values(SourceTypes).map(sourceType => ({
              label: capitalize(sourceType),
              value: sourceType
            })),
            noNoneOption: true,
            value: productSource,
            onChange: onSourceChange
          }}
          brandSelectProps={{
            options: brandsList.map(b => ({
              label: b.name,
              value: b.name
            })),
            noNoneOption: true,
            value: brandToView?.name || '',
            onChange: handlePopupBrandSelect
          }}
          onClose={onPopupSelectorClose}
          onObjectClick={onProductClick}
          objectOptionList={(brandToView?.products || []).map(p => ({
            id: getObjId(p),
            imageSrc: getMainImageSrcForObj(p),
            label: p.name,
            externalId: getObjExternalId(p),
            removable: p.removable
          }))}
          selectedOptionIds={getSelectedObjectList().map(getObjId)}
        />
      )}

      {/* {showPopup &&
        <ObjectsPopupSelector
          title='Manage Attached Products'
          onClose={onPopupSelectorClose}
          dropdownFilterOptions={brandsList.map(b => b.name)}
          dropdownLabel='Brand'
          dropdownPlaceholder='Select brand'
          dropdownOnSelect={handlePopupBrandSelect}
          objectsList={brandToView.products || []}
          selectedObjectsList={getSelectedObjectList()}
          setSelectedList={handleOnPopupSelect}
          // toggleOptions={Object.values(sourceTypes)}
          toggleSwitched={isToggleSwitched}
          onToggleSwitch={onToggleSwitch}
        />
      } */}

    </SectionContainer>
  )
}

const ProductSection = inject(
  PROJECT_PAGE_STORE, BRAND_STORE
)(observer(ProductSectionComponent))
export { ProductSection }
